import { Tooltip } from '@mui/material';
import cn from 'classnames';
import React, { useMemo } from 'react';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { ReactComponent as InventoryIcon } from '../../assets/images/icon-inventory.svg';

import useWindowSize from '../../hooks/useWindowSize';
import { IInventoryItem, InventoryTypesEnum } from '../../types/inventories';
import { getItemCountWarning, isItemOutOfStock } from '../../utils/inventories';

import styles from './ItemCount.module.scss';

interface IProps {
  className?: string;
  count?: number;
  skuOptions?: IInventoryItem[];
  isPreorderAllowed?: boolean;
  type: InventoryTypesEnum;
  threshold?: number;
}
/* A component that shows counts with warning messages
 * Showing counts logic:
 * - Digital items - not showing anything
 * - Physical:
 *    -- showing count and no warning;
 *    -- showing both count value and warning message
 *    -- warning message only if the count value is not defined (-1)
 * - MSKU:
 *    -- don't show a count value
 *    -- show a warning only in case at least one of its SKU options has any warning
 */
const CountIndicator = ({ className, count, skuOptions, isPreorderAllowed, type, threshold }: IProps) => {
  const { mobile } = useWindowSize();

  const isDigital = useMemo(() => type === InventoryTypesEnum.Digital, [type]);
  const isMsku = useMemo(() => type === InventoryTypesEnum.MSKU, [type]);

  const warningMessage = useMemo(
    () =>
      isDigital ? undefined : getItemCountWarning({ count, sku_options: skuOptions, low_stock_threshold: threshold }),
    [isDigital, count, skuOptions, threshold],
  );

  const isItemAvailable = useMemo(
    () => !isItemOutOfStock({ count, sku_options: skuOptions, preorder_allowed: isPreorderAllowed, type }),
    [count, skuOptions, isPreorderAllowed, type],
  );

  const label = useMemo(() => {
    switch (true) {
      case count === -1:
        return undefined;
      default:
        return (count || 0).toFixed();
    }
  }, [count]);

  const hasLabel = useMemo(() => typeof label !== 'undefined', [label]);

  const showCount = useMemo(() => hasLabel && !isMsku, [hasLabel, isMsku]);

  const WarningIconComponent = useMemo(() => {
    if (!warningMessage) {
      return undefined;
    }

    // When both inventory icon and warning/error icon are shown together,
    // make them the same size. Otherwise, use the original sizing.
    const iconStyles = showCount
      ? {
          fontSize: '17px',
          marginLeft: '1px',
          marginBottom: '-0.1em',
        }
      : {
          position: 'static',
          fontSize: mobile ? '25px' : '1rem',
          transform: 'none',
        };

    const WarningIconWrapper = (props: any) => <WarningAmberIcon {...props} color="warning" sx={iconStyles} />;

    const ErrorIconWrapper = (props: any) => <ErrorOutlineIcon {...props} color="error" sx={iconStyles} />;

    return isItemAvailable ? WarningIconWrapper : ErrorIconWrapper;
  }, [warningMessage, isItemAvailable, showCount, mobile]);

  const content = useMemo(() => {
    return !isDigital && (warningMessage || showCount) ? (
      <div
        className={cn(className, styles.container, styles.indicator, {
          [styles.noCount]: !showCount,
        })}
      >
        {showCount && warningMessage ? (
          <>
            <InventoryIcon className={styles.inventoryIcon} />
            <span className={styles.count}>{label}</span>
            {WarningIconComponent && React.createElement(WarningIconComponent)}
          </>
        ) : (
          <>
            {warningMessage && WarningIconComponent && React.createElement(WarningIconComponent)}
            {showCount && (
              <>
                <InventoryIcon className={styles.inventoryIcon} />
                <span className={styles.count}>{label}</span>
              </>
            )}
          </>
        )}
      </div>
    ) : null;
  }, [isDigital, warningMessage, showCount, className, WarningIconComponent, label]);

  return warningMessage && content ? (
    <Tooltip title={warningMessage} arrow placement="top">
      {content}
    </Tooltip>
  ) : (
    content
  );
};

export default CountIndicator;
